import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc, doc, orderBy, serverTimestamp, Timestamp, limit, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Notification, CreateNotificationData, NotificationStats, NotificationSettings } from '../types/notification';

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [settings, setSettings] = useState<NotificationSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  // Értesítések betöltése
  useEffect(() => {
    if (!user?.id || !user?.organizationId) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', user.id),
      where('organizationId', '==', user.organizationId),
      orderBy('createdAt', 'desc'),
      limit(100)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const notificationsData: Notification[] = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
            readAt: data.readAt?.toDate() || undefined,
            expiresAt: data.expiresAt?.toDate() || undefined,
          } as Notification;
        });
        setNotifications(notificationsData);
        setLoading(false);
        setError(null);
      },
      (error) => {
        console.error('Error fetching notifications:', error);
        setError('Failed to load notifications');
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [user?.id, user?.organizationId]);

  // Beállítások betöltése
  useEffect(() => {
    if (!user?.id || !user?.organizationId) {
      setSettings(null);
      return;
    }

    const loadSettings = async () => {
      try {
        const q = query(
          collection(db, 'notificationSettings'),
          where('userId', '==', user.id),
          where('organizationId', '==', user.organizationId)
        );

        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const doc = querySnapshot.docs[0];
          const data = doc.data();
          setSettings({
            id: doc.id,
            ...data,
            updatedAt: data.updatedAt?.toDate() || new Date(),
          } as NotificationSettings);
        } else {
          // Alapértelmezett beállítások létrehozása
          await createDefaultSettings();
        }
      } catch (error) {
        console.error('Error loading notification settings:', error);
      }
    };

    loadSettings();
  }, [user?.id, user?.organizationId]);

  // Alapértelmezett beállítások létrehozása
  const createDefaultSettings = async (): Promise<void> => {
    if (!user?.id || !user?.organizationId) return;

    const defaultSettings: Omit<NotificationSettings, 'id'> = {
      userId: user.id,
      organizationId: user.organizationId,
      emailNotifications: {
        taskAssigned: true,
        taskCompleted: false,
        taskOverdue: true,
        projectUpdates: true,
        deadlineReminders: true,
        systemNotifications: true,
        customerUpdates: true,
        quotationUpdates: true,
        dealUpdates: false,
        contractUpdates: true,
        organizationUpdates: true,
        userUpdates: true,
      },
      inAppNotifications: {
        taskAssigned: true,
        taskCompleted: true,
        taskOverdue: true,
        projectUpdates: true,
        deadlineReminders: true,
        systemNotifications: true,
        mentions: true,
        customerUpdates: true,
        quotationUpdates: true,
        dealUpdates: true,
        contractUpdates: true,
        organizationUpdates: true,
        userUpdates: true,
      },
      soundEnabled: true,
      desktopNotifications: false,
      debugMode: false, // Alapértelmezetten kikapcsolva
      quietHours: {
        enabled: false,
        startTime: '22:00',
        endTime: '08:00',
      },
      updatedAt: new Date(),
    };

    try {
      const docRef = await addDoc(collection(db, 'notificationSettings'), {
        ...defaultSettings,
        updatedAt: serverTimestamp(),
      });

      setSettings({
        id: docRef.id,
        ...defaultSettings,
      });
    } catch (error) {
      console.error('Error creating default settings:', error);
    }
  };

  // Új értesítés létrehozása
  const createNotification = async (data: CreateNotificationData): Promise<string> => {
    try {
      // Call Firebase Function to create notification and send email if enabled
      const createNotificationFn = httpsCallable(functions, 'createNotification');
      const result = await createNotificationFn(data);
      
      const response = result.data as { success: boolean; notificationId: string; emailSent: boolean };
      
      if (response.emailSent) {
        console.log('Email notification sent successfully');
      }
      
      return response.notificationId;
    } catch (error) {
      console.error('Error creating notification:', error);
      throw error;
    }
  };

  // Értesítés megjelölése olvasottként
  const markAsRead = async (notificationId: string): Promise<void> => {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        isRead: true,
        readAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  };

  // Összes értesítés megjelölése olvasottként
  const markAllAsRead = async (): Promise<void> => {
    try {
      const unreadNotifications = notifications.filter(n => !n.isRead);
      const promises = unreadNotifications.map(notification =>
        updateDoc(doc(db, 'notifications', notification.id), {
          isRead: true,
          readAt: serverTimestamp(),
        })
      );

      await Promise.all(promises);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  };

  // Értesítés törlése
  const deleteNotification = async (notificationId: string): Promise<void> => {
    try {
      await deleteDoc(doc(db, 'notifications', notificationId));
    } catch (error) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  };

  // Összes értesítés törlése
  const clearAllNotifications = async (): Promise<void> => {
    try {
      const promises = notifications.map(notification =>
        deleteDoc(doc(db, 'notifications', notification.id))
      );

      await Promise.all(promises);
    } catch (error) {
      console.error('Error clearing all notifications:', error);
      throw error;
    }
  };

  // Beállítások frissítése
  const updateSettings = async (newSettings: Partial<NotificationSettings>): Promise<void> => {
    if (!settings?.id) return;

    try {
      await updateDoc(doc(db, 'notificationSettings', settings.id), {
        ...newSettings,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating notification settings:', error);
      throw error;
    }
  };

  // Értesítési statisztikák
  const getStats = (): NotificationStats => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const thisWeek = new Date(today.getTime() - (today.getDay() * 24 * 60 * 60 * 1000));

    return {
      total: notifications.length,
      unread: notifications.filter(n => !n.isRead).length,
      today: notifications.filter(n => n.createdAt >= today).length,
      thisWeek: notifications.filter(n => n.createdAt >= thisWeek).length,
      overdue: notifications.filter(n => 
        n.type === 'task_overdue' || 
        n.type === 'deadline_reminder'
      ).length,
    };
  };

  // Szűrt értesítések
  const getUnreadNotifications = () => notifications.filter(n => !n.isRead);
  const getRecentNotifications = (count = 5) => notifications.slice(0, count);
  const getNotificationsByType = (type: Notification['type']) => 
    notifications.filter(n => n.type === type);

  return {
    notifications,
    settings,
    loading,
    error,
    createNotification,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
    updateSettings,
    getStats,
    getUnreadNotifications,
    getRecentNotifications,
    getNotificationsByType,
  };
}; 